<script setup>
import { formatNumber, formatRupiah } from '@/composables/useFormat';
import { useApi } from '@/utils/api'; // ← GANTI dari $api ke useApi

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Report',
  },
})

// ── State ─────────────────────────────────────────────────────────────────────
const loading    = ref(false)
const dateFrom   = ref('')
const dateTo     = ref('')
const search     = ref('')
const filterLow  = ref(false)

const summary = ref({ total_stock_value: 0, low_stock_count: 0 })
const items   = ref([])

// ── Table ──────────────────────────────────────────────────────────────────────
const headers = [
  { title: 'Bahan Baku', key: 'name' },
  { title: 'Kategori', key: 'category', sortable: false },
  { title: 'Stok Saat Ini', key: 'current_stock', align: 'end' },
  { title: 'Min Stok', key: 'minimum_stock', align: 'end' },
  { title: 'Avg Cost', key: 'average_cost', align: 'end' },
  { title: 'Nilai Stok', key: 'stock_value', align: 'end' },
  { title: 'Masuk', key: 'period_in', align: 'end' },
  { title: 'Keluar', key: 'period_out', align: 'end' },
  { title: 'Status', key: 'is_low_stock', align: 'center', sortable: false },
]

// ── Fetch ─────────────────────────────────────────────────────────────────────
const fetchReport = async () => {
  loading.value = true
  try {
    const query = {}
    if (dateFrom.value) query.date_from = dateFrom.value
    if (dateTo.value)   query.date_to   = dateTo.value

    // ✅ FIX: Pakai useApi + URL tanpa prefix /api
    const response = await useApi('/v1/reports/inventory', { query }).get()
    const data = response?.data ?? response

    items.value   = data?.items ?? []
    summary.value = {
      total_stock_value: data?.total_stock_value ?? 0,
      low_stock_count:   data?.low_stock_count   ?? 0,
    }
  } catch (err) {
    console.error('❌ Inventory report error:', err)
  } finally {
    loading.value = false
  }
}

onMounted(fetchReport)

// ── Computed / Filter ─────────────────────────────────────────────────────────
const filteredItems = computed(() => {
  let list = items.value

  if (search.value) {
    const q = search.value.toLowerCase()
    list = list.filter(i =>
      i.name.toLowerCase().includes(q) ||
      (i.sku ?? '').toLowerCase().includes(q) ||
      (i.category ?? '').toLowerCase().includes(q)
    )
  }

  if (filterLow.value) {
    list = list.filter(i => i.is_low_stock)
  }

  return list
})

const exportFile = (format) => {
  const params = new URLSearchParams()
  if (dateFrom.value) params.set('date_from', dateFrom.value)
  if (dateTo.value)   params.set('date_to', dateTo.value)
  window.open(`/api/v1/export/inventory/${format}?${params}`, '_blank')
}
</script>

<template>
  <div>
    <!-- Page Header -->
    <VRow class="mb-4 align-center">
      <VCol cols="12" md="6">
        <h4 class="text-h4 font-weight-medium mb-1">Laporan Inventory</h4>
        <p class="text-body-2 text-medium-emphasis">Status stok dan pergerakan bahan baku</p>
      </VCol>
    </VRow>

    <!-- Filter -->
    <VCard class="mb-4">
      <VCardText>
        <VRow align="center">
          <VCol cols="12" sm="3">
            <VTextField
              v-model="dateFrom"
              type="date"
              label="Dari Tanggal"
              density="compact"
              hide-details
              clearable
            />
          </VCol>
          <VCol cols="12" sm="3">
            <VTextField
              v-model="dateTo"
              type="date"
              label="Sampai Tanggal"
              density="compact"
              hide-details
              clearable
            />
          </VCol>
          <VCol cols="12" sm="2">
            <VBtn
              color="primary"
              block
              :loading="loading"
              prepend-icon="ri-refresh-line"
              @click="fetchReport"
            >
              Refresh
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Summary Cards -->
    <VRow class="mb-4">
      <VCol cols="12" sm="6">
        <VCard>
          <VCardText class="d-flex align-center gap-3">
            <VAvatar color="primary" variant="tonal" size="44">
              <VIcon icon="ri-archive-line" />
            </VAvatar>
            <div>
              <div class="text-caption text-medium-emphasis">Total Nilai Stok</div>
              <div class="text-h6 font-weight-bold text-primary">
                {{ formatRupiah(summary.total_stock_value) }}
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
      <VCol cols="12" sm="6">
        <VCard>
          <VCardText class="d-flex align-center gap-3">
            <VAvatar :color="summary.low_stock_count > 0 ? 'error' : 'success'" variant="tonal" size="44">
              <VIcon icon="ri-error-warning-line" />
            </VAvatar>
            <div>
              <div class="text-caption text-medium-emphasis">Stok Kritis</div>
              <div
                class="text-h6 font-weight-bold"
                :class="summary.low_stock_count > 0 ? 'text-error' : 'text-success'"
              >
                {{ formatNumber(summary.low_stock_count) }} item
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Table Card -->
    <VCard :loading="loading">
      <VCardText class="d-flex align-center gap-3 flex-wrap pb-2">
        <VTextField
          v-model="search"
          placeholder="Cari bahan baku..."
          prepend-inner-icon="ri-search-line"
          density="compact"
          hide-details
          clearable
          style="max-width: 280px"
        />

        <VSwitch
          v-model="filterLow"
          label="Tampilkan stok kritis saja"
          color="error"
          density="compact"
          hide-details
          class="ms-2"
        />

        <VSpacer />

        <VBtnGroup density="compact" variant="outlined" divided>
          <VBtn
            prepend-icon="ri-file-excel-line"
            color="success"
            size="small"
            @click="exportFile('excel')"
          >
            Excel
          </VBtn>
          <VBtn
            prepend-icon="ri-file-pdf-line"
            color="error"
            size="small"
            @click="exportFile('pdf')"
          >
            PDF
          </VBtn>
        </VBtnGroup>
      </VCardText>

      <VDivider />

      <VDataTable
        :headers="headers"
        :items="filteredItems"
        :loading="loading"
        density="compact"
        hover
        :items-per-page="25"
      >
        <template #item.name="{ item }">
          <div>
            <div class="text-body-2 font-weight-medium">{{ item.name }}</div>
            <div class="text-caption text-medium-emphasis">{{ item.sku }}</div>
          </div>
        </template>

        <template #item.current_stock="{ item }">
          <span :class="item.is_low_stock ? 'text-error font-weight-bold' : ''">
            {{ formatNumber(item.current_stock) }} {{ item.unit }}
          </span>
        </template>

        <template #item.minimum_stock="{ item }">
          {{ formatNumber(item.minimum_stock) }} {{ item.unit }}
        </template>

        <template #item.average_cost="{ item }">
          {{ formatRupiah(item.average_cost) }}
        </template>

        <template #item.stock_value="{ item }">
          <span class="font-weight-medium text-primary">{{ formatRupiah(item.stock_value) }}</span>
        </template>

        <template #item.period_in="{ item }">
          <span class="text-success">+{{ formatNumber(item.period_in) }}</span>
        </template>

        <template #item.period_out="{ item }">
          <span class="text-error">-{{ formatNumber(item.period_out) }}</span>
        </template>

        <template #item.is_low_stock="{ item }">
          <VChip
            :color="item.is_low_stock ? 'error' : 'success'"
            size="small"
            variant="tonal"
          >
            {{ item.is_low_stock ? 'Kritis' : 'Aman' }}
          </VChip>
        </template>

        <!-- Empty state -->
        <template #no-data>
          <div class="text-center py-8">
            <VIcon icon="ri-archive-line" size="48" color="secondary" class="mb-2" />
            <p class="text-body-2 text-medium-emphasis">Tidak ada data inventory</p>
          </div>
        </template>
      </VDataTable>
    </VCard>
  </div>
</template>
